import { Component, Inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-change-password-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
],
  template: `
    <div class="change-password-dialog crm-dialog">
      <h2 mat-dialog-title>
        <mat-icon>lock</mat-icon>
        Jelszó megváltoztatása
      </h2>
    
      <mat-dialog-content>
        <form class="password-form">
          <!-- Jelenlegi jelszó -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Jelenlegi jelszó</mat-label>
            <input matInput
              [type]="hideCurrentPassword ? 'password' : 'text'"
              [(ngModel)]="currentPassword"
              name="currentPassword"
              autocomplete="current-password"
              required
              placeholder="Jelenlegi jelszó">
            <button mat-icon-button matSuffix
              (click)="hideCurrentPassword = !hideCurrentPassword"
              type="button">
              <mat-icon>{{hideCurrentPassword ? 'visibility' : 'visibility_off'}}</mat-icon>
            </button>
          </mat-form-field>
    
          <!-- Új jelszó -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Új jelszó</mat-label>
            <input matInput
              [type]="hideNewPassword ? 'password' : 'text'"
              [(ngModel)]="newPassword"
              name="newPassword"
              autocomplete="new-password"
              required
              placeholder="Új jelszó">
            <button mat-icon-button matSuffix
              (click)="hideNewPassword = !hideNewPassword"
              type="button">
              <mat-icon>{{hideNewPassword ? 'visibility' : 'visibility_off'}}</mat-icon>
            </button>
          </mat-form-field>
    
          <!-- Új jelszó megerősítése -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Új jelszó megerősítése</mat-label>
            <input matInput
              [type]="hideConfirmPassword ? 'password' : 'text'"
              [(ngModel)]="confirmPassword"
              name="confirmPassword"
              autocomplete="new-password"
              required
              placeholder="Új jelszó megerősítése">
            <button mat-icon-button matSuffix
              (click)="hideConfirmPassword = !hideConfirmPassword"
              type="button">
              <mat-icon>{{hideConfirmPassword ? 'visibility' : 'visibility_off'}}</mat-icon>
            </button>
          </mat-form-field>
    
          <!-- Hibaüzenet -->
          @if (errorMessage) {
            <div class="error-message">
              <mat-icon>error</mat-icon>
              {{ errorMessage }}
            </div>
          }
    
          <!-- Jelszó követelmények -->
          <div class="password-requirements">
            <h4>Jelszó követelmények:</h4>
            <ul>
              <li [class.valid]="isMinLengthValid()">Legalább 8 karakter</li>
              <li [class.valid]="hasUpperCase()">Legalább 1 nagybetű</li>
              <li [class.valid]="hasLowerCase()">Legalább 1 kisbetű</li>
              <li [class.valid]="hasNumber()">Legalább 1 szám</li>
              <li [class.valid]="hasSpecialChar()">Legalább 1 speciális karakter</li>
            </ul>
          </div>
        </form>
      </mat-dialog-content>
    
      <mat-dialog-actions align="end">
        <button mat-button (click)="onCancel()" [disabled]="isLoading">
          Mégse
        </button>
        <button mat-raised-button
          color="primary"
          (click)="onSave()"
          [disabled]="!isFormValid() || isLoading">
          @if (isLoading) {
            <mat-spinner diameter="20"></mat-spinner>
          }
          @if (!isLoading) {
            <mat-icon>save</mat-icon>
          }
          {{ isLoading ? 'Mentés...' : 'Jelszó mentése' }}
        </button>
      </mat-dialog-actions>
    </div>
    `,
  styles: [`
    .change-password-dialog {
      min-width: 400px;
      max-width: 500px;
    }

    .password-form {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin: 16px 0;
    }

    .full-width {
      width: 100%;
    }

    .error-message {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #f44336;
      font-size: 14px;
      margin-top: 8px;
    }

    .password-requirements {
      margin-top: 16px;
      padding: 16px;
      background-color: #f5f5f5;
      border-radius: 8px;
    }

    .password-requirements h4 {
      margin: 0 0 8px 0;
      font-size: 14px;
      font-weight: 500;
    }

    .password-requirements ul {
      margin: 0;
      padding-left: 16px;
      font-size: 12px;
    }

    .password-requirements li {
      color: #666;
      margin-bottom: 4px;
    }

    .password-requirements li.valid {
      color: #4caf50;
      font-weight: 500;
    }

    mat-dialog-actions {
      margin-bottom: 0;
    }

    mat-spinner {
      margin-right: 8px;
    }
  `]
})
export class ChangePasswordDialog {
  currentPassword: string = '';
  newPassword: string = '';
  confirmPassword: string = '';
  hideCurrentPassword: boolean = true;
  hideNewPassword: boolean = true;
  hideConfirmPassword: boolean = true;
  errorMessage: string = '';
  isLoading: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ChangePasswordDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  isMinLengthValid(): boolean {
    return this.newPassword.length >= 8;
  }

  hasUpperCase(): boolean {
    return /[A-Z]/.test(this.newPassword);
  }

  hasLowerCase(): boolean {
    return /[a-z]/.test(this.newPassword);
  }

  hasNumber(): boolean {
    return /\d/.test(this.newPassword);
  }

  hasSpecialChar(): boolean {
    return /[!@#$%^&*(),.?":{}|<>]/.test(this.newPassword);
  }

  isPasswordValid(): boolean {
    return this.isMinLengthValid() && 
           this.hasUpperCase() && 
           this.hasLowerCase() && 
           this.hasNumber() && 
           this.hasSpecialChar();
  }

  isFormValid(): boolean {
    return this.currentPassword.length > 0 &&
           this.isPasswordValid() &&
           this.newPassword === this.confirmPassword;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    this.errorMessage = '';
    
    if (!this.isFormValid()) {
      this.errorMessage = 'Kérlek töltsd ki helyesen az összes mezőt!';
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.errorMessage = 'Az új jelszavak nem egyeznek!';
      return;
    }

    this.isLoading = true;
    this.dialogRef.close({
      currentPassword: this.currentPassword,
      newPassword: this.newPassword
    });
  }
}